import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, NgZone, PLATFORM_ID, inject } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class CentaurTableOverflowTitleService {
  private _document = inject(DOCUMENT);
  private _platformId = inject(PLATFORM_ID);
  private _ngZone = inject(NgZone);
  private _mutationObserver: MutationObserver | null = null;
  private _resizeObserver: ResizeObserver | null = null;
  private _refreshFrame: number | null = null;

  constructor() {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }

    this._ngZone.runOutsideAngular(() => {
      this._scheduleRefresh();

      this._mutationObserver = new MutationObserver(() => this._scheduleRefresh());
      this._mutationObserver.observe(this._document.body, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      if (typeof ResizeObserver !== 'undefined') {
        this._resizeObserver = new ResizeObserver(() => this._scheduleRefresh());
        this._resizeObserver.observe(this._document.body);
      } else {
        window.addEventListener('resize', this._handleResize, { passive: true });
      }
    });
  }

  private _handleResize = (): void => {
    this._scheduleRefresh();
  };

  private _scheduleRefresh(): void {
    if (this._refreshFrame !== null) {
      return;
    }

    this._refreshFrame = window.requestAnimationFrame(() => {
      this._refreshFrame = null;
      this._refreshTitles();
    });
  }

  private _refreshTitles(): void {
    const cells = this._document.querySelectorAll<HTMLElement>(
      'th.centaur-table__truncate, td.centaur-table__truncate',
    );

    cells.forEach((cell) => this._refreshCellTitle(cell));
  }

  private _refreshCellTitle(cell: HTMLElement): void {
    if (this._shouldSkipCell(cell)) {
      cell.removeAttribute('data-centaur-overflow');
      return;
    }

    const overflowTarget = this._getOverflowTarget(cell);
    const cellText = overflowTarget.textContent?.replace(/\s+/g, ' ').trim() ?? '';

    if (!cellText) {
      cell.removeAttribute('data-centaur-overflow');
      return;
    }

    const isOverflowed =
      overflowTarget.scrollWidth > overflowTarget.clientWidth + 1 ||
      cell.scrollWidth > cell.clientWidth + 1;

    if (isOverflowed) {
      cell.setAttribute('data-centaur-overflow', 'true');
      return;
    }

    cell.removeAttribute('data-centaur-overflow');
  }

  private _shouldSkipCell(cell: HTMLElement): boolean {
    // If the cell explicitly provides a truncate-text span it has opted into the
    // centaur overflow/tooltip pattern, so process it even when an interactive
    // control (e.g. an overlaid input) is also present.
    const hasTruncateText = Boolean(
      cell.querySelector(':scope > .centaur-table__truncate-text'),
    );
    if (hasTruncateText) {
      return false;
    }
    return Boolean(
      cell.querySelector(
        'input, textarea, select, .p-inputtext, .p-select, .p-datepicker, .p-checkbox',
      ),
    );
  }

  private _getOverflowTarget(cell: HTMLElement): HTMLElement {
    return (
      cell.querySelector<HTMLElement>(':scope > .centaur-table__truncate-text') ??
      cell.querySelector<HTMLElement>(
        ':scope > a, :scope > span, :scope > div, :scope > p',
      ) ?? cell
    );
  }
}